import type { CanvasElement, GroupElement } from '../types/index';

function isGroup(el: CanvasElement): el is GroupElement {
  return el.type === 'group';
}

/**
 * Given a seed list of element ids, returns every element in those ids
 * plus (recursively) every descendant referenced by GroupElement.childIds.
 * Result is de-duplicated and preserves the relative order of the input
 * elements array. Unknown ids are silently skipped.
 */
export function expandSubtree(
  elements: CanvasElement[],
  rootIds: string[]
): CanvasElement[] {
  const byId = new Map(elements.map((e) => [e.id, e] as const));
  const collected = new Set<string>();

  const visit = (id: string) => {
    if (collected.has(id)) return;
    const el = byId.get(id);
    if (!el) return;
    collected.add(id);
    if (isGroup(el)) {
      for (const childId of el.childIds) visit(childId);
    }
  };

  for (const id of rootIds) visit(id);

  return elements.filter((e) => collected.has(e.id));
}

/**
 * Deep-clones a subtree of elements and rewrites every id to a fresh uuid.
 * Rewrites GroupElement.childIds through the id map so internal references
 * remain intact. External references (ids not present in the subtree) are
 * dropped from childIds rather than left dangling.
 */
export function cloneSubtreeWithNewIds(
  subtree: CanvasElement[]
): { elements: CanvasElement[]; idMap: Map<string, string> } {
  const idMap = new Map<string, string>();
  for (const el of subtree) idMap.set(el.id, crypto.randomUUID());

  const cloned: CanvasElement[] = subtree.map((el) => {
    const copy = JSON.parse(JSON.stringify(el)) as CanvasElement;
    copy.id = idMap.get(el.id)!;
    if (isGroup(copy)) {
      copy.childIds = copy.childIds
        .map((cid) => idMap.get(cid))
        .filter((v): v is string => typeof v === 'string');
    }
    return copy;
  });

  return { elements: cloned, idMap };
}

/**
 * Returns a shallow-copied elements array with the given removed id pruned
 * from any GroupElement.childIds list. Non-group elements are untouched.
 */
export function pruneChildIdFromGroups(
  elements: CanvasElement[],
  removedId: string
): CanvasElement[] {
  let mutated = false;
  const next = elements.map((el) => {
    if (!isGroup(el)) return el;
    if (!el.childIds.includes(removedId)) return el;
    mutated = true;
    return { ...el, childIds: el.childIds.filter((id) => id !== removedId) };
  });
  return mutated ? next : elements;
}
